import { useEffect, useRef, useState, useCallback } from 'react';
import { VOICE_CHECK_INTERVAL } from '../config/constants';

interface VoiceState {
  isSpeaking: boolean;
  rmsValue: number;
  voiceSDR: number;
  continuousSpeechSec: number;
}

interface VoiceBufferEntry {
  t: number;
  speaking: boolean;
}

/**
 * Voice sensor using AudioContext + RMS-based VAD.
 */
export function useVoiceSensor() {
  const [voiceState, setVoiceState] = useState<VoiceState>({
    isSpeaking: false,
    rmsValue: 0,
    voiceSDR: 0,
    continuousSpeechSec: 0,
  });

  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrRef = useRef<Uint8Array | null>(null);
  const bufferRef = useRef<VoiceBufferEntry[]>([]);
  const thresholdRef = useRef(0.06); // 캘리브 전 기본값: 일반 배경소음보다 높게
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasInitializedRef = useRef(false);

  // Calibration data collection
  const calibRmsRef = useRef<number[]>([]);
  const isCalibRef = useRef(false);

  const startCalibCollection = useCallback(() => {
    calibRmsRef.current = [];
    isCalibRef.current = true;
  }, []);

  const stopCalibCollection = useCallback((): number[] => {
    isCalibRef.current = false;
    return calibRmsRef.current;
  }, []);

  const setThreshold = useCallback((t: number) => {
    thresholdRef.current = t;
  }, []);

  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    let disposed = false;

    async function init() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (disposed) return;

        const audioCtx = new AudioContext();
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 2048;
        const source = audioCtx.createMediaStreamSource(stream);
        source.connect(analyser);

        const dataArr = new Uint8Array(analyser.fftSize);

        audioCtxRef.current = audioCtx;
        analyserRef.current = analyser;
        dataArrRef.current = dataArr;

        intervalRef.current = setInterval(() => {
          if (disposed || !analyserRef.current || !dataArrRef.current) return;

          analyserRef.current.getByteTimeDomainData(dataArrRef.current);
          let sum = 0;
          for (let i = 0; i < dataArrRef.current.length; i++) {
            const v = (dataArrRef.current[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / dataArrRef.current.length);

          if (isCalibRef.current) {
            calibRmsRef.current.push(rms);
          }

          const speaking = rms > thresholdRef.current;
          const now = Date.now();
          bufferRef.current.push({ t: now, speaking });
          // Keep 60s of data
          const cutoff = now - 60000;
          while (bufferRef.current.length > 0 && bufferRef.current[0].t < cutoff) {
            bufferRef.current.shift();
          }

          // SDR: speaking ratio in buffer
          const speakingCount = bufferRef.current.filter(v => v.speaking).length;
          const sdr = bufferRef.current.length > 0 ? (speakingCount / bufferRef.current.length) * 100 : 0;

          // Continuous speech: count consecutive speaking entries from end
          let contMs = 0;
          for (let i = bufferRef.current.length - 1; i >= 0; i--) {
            if (!bufferRef.current[i].speaking) break;
            contMs = now - bufferRef.current[i].t;
          }

          setVoiceState({
            isSpeaking: speaking,
            rmsValue: rms,
            voiceSDR: sdr,
            continuousSpeechSec: contMs / 1000,
          });
        }, VOICE_CHECK_INTERVAL);
      } catch (err) {
        console.warn('[useVoiceSensor] Failed to initialize:', err);
      }
    }

    init();

    return () => {
      disposed = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      audioCtxRef.current?.close();
    };
  }, []);

  return {
    ...voiceState,
    startCalibCollection,
    stopCalibCollection,
    setThreshold,
  };
}
